import React from "react";
import { motion } from "framer-motion";
import "./FinancialProjection.css";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  FaUsers, FaMoneyBillWave, FaShoppingCart, FaBullseye,
  FaChartBar, FaCheckCircle, FaHandshake, FaTrophy
} from "react-icons/fa";

const FinancialProjections = () => {
  const { translations: tRaw } = useLanguage();
  const t = {
    ...tRaw,
    investment: {
      ...(tRaw?.investment || {}),
      financial: {
        ...(tRaw?.investment?.financial || {}),
        metricTitles: {
          ...(tRaw?.investment?.financial?.metricTitles || {}),
        },
        years: {
          ...(tRaw?.investment?.financial?.years || {}),
        },
      },
    },
  };

  const projections = [
    {
      key: "year1",
      year: t.investment?.financial?.years?.year1?.label || "First Year",
      users: t.investment?.financial?.years?.year1?.users || "500,000",
      revenuePerUser: t.investment?.financial?.years?.year1?.revenuePerUser || "USD 12/month",
      userRevenue: t.investment?.financial?.years?.year1?.userRevenue || "USD 6,000,000",
      adRevenue: t.investment?.financial?.years?.year1?.adRevenue || "USD 2,723,163",
      totalRevenue: t.investment?.financial?.years?.year1?.totalRevenue || "USD 8,723,163",
      profitAfterCosts: t.investment?.financial?.years?.year1?.profitAfterCosts || "USD 5,233,898",
      investorShare: t.investment?.financial?.years?.year1?.investorShare || "20% = USD 1,046,779",
      netProfit: t.investment?.financial?.years?.year1?.netProfit || "USD 515,147 (after 2nd payment)",
    },
    {
      key: "year2",
      year: t.investment?.financial?.years?.year2?.label || "Second Year",
      users: t.investment?.financial?.years?.year2?.users || "1,250,000",
      revenuePerUser: t.investment?.financial?.years?.year2?.revenuePerUser || "USD 12/month",
      userRevenue: t.investment?.financial?.years?.year2?.userRevenue || "USD 15,000,000",
      adRevenue: t.investment?.financial?.years?.year2?.adRevenue || "USD 3,540,112",
      totalRevenue: t.investment?.financial?.years?.year2?.totalRevenue || "USD 18,540,112",
      profitAfterCosts: t.investment?.financial?.years?.year2?.profitAfterCosts || "USD 11,124,067",
      investorShare: t.investment?.financial?.years?.year2?.investorShare || "20% = USD 2,224,813",
      netProfit: t.investment?.financial?.years?.year2?.netProfit, // optional in Y2
    },
  ];

  const metricTitles = {
    users: t.investment?.financial?.metricTitles?.users || "Active Users",
    revenuePerUser: t.investment?.financial?.metricTitles?.revenuePerUser || "Revenue/User",
    userRevenue: t.investment?.financial?.metricTitles?.userRevenue || "Users Revenue",
    adRevenue: t.investment?.financial?.metricTitles?.adRevenue || "Ads Revenue",
    totalRevenue: t.investment?.financial?.metricTitles?.totalRevenue || "Total Revenue",
    profitAfterCosts: t.investment?.financial?.metricTitles?.profitAfterCosts || "Net Profit (60%)",
    investorShare: t.investment?.financial?.metricTitles?.investorShare || "Investor Share",
    netProfit: t.investment?.financial?.metricTitles?.netProfit || "Final Net Profit",
  };

  const metrics = [
    { icon: <FaUsers />, name: "users" },
    { icon: <FaMoneyBillWave />, name: "revenuePerUser" },
    { icon: <FaShoppingCart />, name: "userRevenue" },
    { icon: <FaBullseye />, name: "adRevenue" },
    { icon: <FaChartBar />, name: "totalRevenue" },
    { icon: <FaCheckCircle />, name: "profitAfterCosts" },
    { icon: <FaHandshake />, name: "investorShare" },
    { icon: <FaTrophy />, name: "netProfit" },
  ];

  return (
    <div className="financial-projections-section">
      <div className="projections-container">
        <motion.h2
          className="projections-title"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {t.investment?.financial?.title || t.investment?.financialTitle || "Financial Projections"}
        </motion.h2>

        <div className="timeline-container">
          <div className="timeline-line"></div>

          {projections.map((year, idx) => (
            <motion.div
              key={year.key}
              className="timeline-card"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.2 }}
            >
              <div className="year-badge">
                <span>{year.year}</span>
              </div>

              <div className="metrics-grid">
                {metrics.map((metric, i) =>
                  year[metric.name] ? (
                    <motion.div
                      key={`${year.key}-${metric.name}`}
                      className="metric-item"
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: 1 }}
                      transition={{ delay: i * 0.1 + idx * 0.15 }}
                    >
                      <div className="metric-icon">{metric.icon}</div>
                      <div className="metric-content">
                        <h4 className="metric-title">
                          {metricTitles[metric.name]}
                        </h4>
                        <p className="metric-value">{year[metric.name]}</p>
                      </div>
                    </motion.div>
                  ) : null
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FinancialProjections;
